/**
 * EGY CÉG KAPCSOLATTARTÓI — a Cégek szelet alól nyílik (#32).
 *
 * ═══ MIÉRT ITT, ÉS NEM A FELHASZNÁLÓK FÜLÖN ═══
 *
 * Viktor döntése (2026-09-14): az ügyfél-kapcsolattartó a CÉG alatt kap
 * felvételt. Így a 09-11-i döntés érvényben marad — az `OFFICE_ROLES` nem
 * bővül `CLIENT`-tel —, és a két identitás nem mosódik össze. A Felhasználók
 * fül listája továbbra is MUTATJA az ügyfeleket; csak felvenni nem ott lehet.
 *
 * ⚠️ ÉS EZ NEM NEGYEDIK FELÜLET. Nem új tengely: ugyanaz a cég-tengely,
 * egy sorral mélyebben — ahogy a `CompanyForm` is a céglista alól nyílik. A
 * `CLAUDE.md` §2 a HÁROM MUNKAKÉPERNYŐRŐL szól, a Beállítások lap pedig
 * adminisztráció.
 *
 * ═══ A VISSZAÚT MEGNEVEZVE, NEM EGY × JEL ═══
 *
 * Ugyanaz az érv, mint a lap „Vissza a munkához" gombjánál: a fülsáv helyén
 * itt a cég neve áll, tehát a „hol vagyok" kérdésre egy pillanatra nincs
 * válasz a megszokott helyen. Az `Escape` ugyanezt teszi — a réteget a lap
 * regisztere zárja (`useSettingsLayer`), nem egy saját `onKeyDown`.
 */

import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from './ConfirmDialog';
import { ContactForm } from './ContactForm';
import type { CompanyRow } from './useCompanies';
import { type ContactRow, type ContactsState, useCompanyContacts } from './useCompanyContacts';
import { type LayerRegistrar, useSettingsLayer } from './useSettingsLayer';

type Translate = ReturnType<typeof useTranslation>['t'];

/** Az állapotváltás DÖNTÉS, nem adatbevitel — megerősítést kér. */
type Confirming = { contact: ContactRow; active: boolean } | null;

export interface CompanyContactsProps {
  company: CompanyRow;
  /** A visszaút a céglistára — a gomb és az `Escape` is ezt hívja. */
  onBack: () => void;
  onLayer: LayerRegistrar;
}

export function CompanyContacts({ company, onBack, onLayer }: CompanyContactsProps) {
  const { t } = useTranslation();
  const state = useCompanyContacts(company.id);
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState<Confirming>(null);

  /** Mentés után, „Mégse"-re és `Escape`-re is — `CompanySettings` `closeForm`. */
  const closeForm = () => {
    state.clearWriteError();
    setAdding(false);
  };

  const blocked = blockingView(state, t);

  useSettingsLayer(onLayer, {
    hidden: blocked !== null,
    confirming: confirming !== null,
    editing: adding,
    saving: state.status === 'saving',
    closeConfirm: () => setConfirming(null),
    closeForm,
    // ⚠️ AMI NÉLKÜL AZ `Escape` A LAPOT ZÁRNÁ. Nyitott réteg nélkül a
    // „visszalépés" itt nem a lap bezárása, hanem a céglista.
    fallbackClose: onBack,
  });

  const inactiveCount = state.contacts.filter((contact) => !contact.active).length;

  return (
    <>
      <div className="settings__head">
        <div>
          <h2>{t('contacts.title', { company: company.name })}</h2>
          <p className="subtitle">{t('contacts.subtitle')}</p>
        </div>
        <button type="button" onClick={onBack}>
          ← {t('contacts.back')}
          <kbd className="tab__kbd">Esc</kbd>
        </button>
      </div>

      {blocked ??
        (adding ? (
          <ContactForm companyName={company.name} state={state} onDone={closeForm} />
        ) : (
          <>
            <div className="settings__toolbar">
              <span className="spacer" />
              <button
                type="button"
                className="settings__primary"
                // ⚠️ INAKTÍV CÉGHEZ NEM VESZÜNK FEL — a szerver 409-cel
                // utasítja el, és a gomb itt ezt előre megmondja. A tiltás
                // nem a kikényszerítés (az a szerveré), csak a zsákutca
                // elkerülése.
                disabled={!company.active}
                onClick={() => setAdding(true)}
              >
                {t('contacts.new')}
              </button>
            </div>

            {company.active ? null : <p className="hint">{t('contacts.inactiveCompanyHint')}</p>}

            <InvitationNoticeView state={state} />
            <WriteError state={state} />

            {state.contacts.length === 0 ? (
              <p className="empty">{t('contacts.empty')}</p>
            ) : (
              <table className="list">
                <thead>
                  <tr>
                    <th>{t('contacts.column.name')}</th>
                    <th>{t('contacts.column.state')}</th>
                    <th>{/* műveletek — fejléc nélkül, nem adat */}</th>
                  </tr>
                </thead>
                <tbody>
                  {state.contacts.map((contact) => (
                    <ContactRowView
                      key={contact.id}
                      contact={contact}
                      onResend={() => state.resendInvitation(contact)}
                      onToggle={() => setConfirming({ contact, active: !contact.active })}
                    />
                  ))}
                </tbody>
              </table>
            )}

            <p className="hint">
              {t('contacts.count', { count: state.contacts.length })}
              {inactiveCount > 0
                ? ` — ${t('contacts.countSplit', {
                    active: state.contacts.length - inactiveCount,
                    inactive: inactiveCount,
                  })}`
                : ''}
              {' · '}
              {t('contacts.inactiveNote')}
            </p>
          </>
        ))}

      {confirming ? (
        /* A szöveg a KÖVETKEZMÉNYT mondja el, nem azt, hogy „biztos vagy
           benne": az inaktivált kapcsolattartó kiesik a bekérő levelek
           címzettjei közül, és az élő linkje is érvénytelenné válik — sem az
           egyik, sem a másik nem látszik a gombon. */
        <ConfirmDialog
          message={
            confirming.active
              ? t('contacts.reactivateConfirm', { name: confirming.contact.name })
              : t('contacts.deactivateConfirm', { name: confirming.contact.name })
          }
          yesLabel={t('contacts.confirmYes')}
          noLabel={t('contacts.confirmNo')}
          onYes={() => {
            state.setActive(confirming.contact.id, confirming.active);
            setConfirming(null);
          }}
          onNo={() => setConfirming(null)}
        />
      ) : null}
    </>
  );
}

function ContactRowView({
  contact,
  onResend,
  onToggle,
}: {
  contact: ContactRow;
  onResend: () => void;
  onToggle: () => void;
}) {
  const { t } = useTranslation();

  return (
    /* ⚠️ AZ INAKTÍV SOR NEM HALVÁNYUL EL — visszahúzott háttér, teljes
       erősségű szöveg (`design-tokens.md`: az opacity nem lefokozó csatorna). */
    <tr className={contact.active ? 'row' : 'row row--inactive'}>
      <td>
        {contact.name}
        <span className="row__note">{contact.email}</span>
      </td>
      <td>{stateLabel(t, contact)}</td>
      <td className="row__actions">
        <span className="row__buttons">
          {/* „MEGHÍVÓ ÚJRAKÜLDÉSE" csak annál, aki még SOHA nem lépett be. A
              beváltás belépést is rögzít, tehát ez a jel pontos; ha mégsem, a
              szerver 409-et ad, és a mondata látszik. */}
          {contact.active && contact.lastLoginAt === null ? (
            <button type="button" onClick={onResend}>
              {t('contacts.resend')}
            </button>
          ) : null}
          <button type="button" onClick={onToggle}>
            {contact.active ? t('contacts.deactivate') : t('contacts.reactivate')}
          </button>
        </span>
      </td>
    </tr>
  );
}

function stateLabel(t: Translate, contact: ContactRow): string {
  if (!contact.active) {
    return t('contacts.inactive');
  }
  return contact.lastLoginAt === null ? t('contacts.pending') : t('contacts.active');
}

/**
 * A MEGHÍVÓ SORSA — a felvétel és az újraküldés után.
 *
 * ⚠️ A `failed` HIBAKÉNT JELENIK MEG, DE NEM A FELVÉTEL HIBÁJAKÉNT: a
 * kapcsolattartó a listában áll, és a sorában ott a „Meghívó újraküldése".
 */
function InvitationNoticeView({ state }: { state: ContactsState }) {
  const { t } = useTranslation();

  if (!state.invitation) {
    return null;
  }

  const { outcome, email } = state.invitation;
  const dismiss = (
    <button type="button" onClick={state.dismissInvitation}>
      {t('contacts.dismiss')}
    </button>
  );

  return outcome === 'sent' ? (
    <output className="notice">
      {t('contacts.invitationSent', { email })} {dismiss}
    </output>
  ) : (
    <p className="error" role="alert">
      {t('contacts.invitationFailed', { email })} {dismiss}
    </p>
  );
}

/**
 * A SOR-MŰVELETEK HIBÁJA — inaktiválás, újraküldés.
 *
 * ⚠️ A 403, A 404 ÉS A 409 A SZERVER MONDATÁT MUTATJA, SZÓ SZERINT: „ennek a
 * cégnek nincs ilyen kapcsolattartója" (a lista elavult), „már beállította a
 * jelszavát". Egy saját „nem sikerült" ezeknél elnyelné a teendőt.
 */
function WriteError({ state }: { state: ContactsState }) {
  const { t } = useTranslation();

  if (state.status === 'failed') {
    return (
      <p className="error" role="alert">
        {t('contacts.stateFailed')}
      </p>
    );
  }
  if (state.status === 'refused' || state.status === 'conflict' || state.status === 'invalid') {
    return (
      <p className="error" role="alert">
        {state.detail}
      </p>
    );
  }
  return null;
}

/** A három ág, ami megállítja a nézetet — betöltés, tiltás, hibás betöltés. */
function blockingView(state: ContactsState, t: Translate): ReactElement | null {
  if (state.status === 'forbidden') {
    return (
      <p className="error" role="alert">
        {t('contacts.forbidden')}
      </p>
    );
  }

  if (state.status === 'loading' && state.contacts.length === 0) {
    return <p className="subtitle">{t('contacts.loading')}</p>;
  }

  if (state.status === 'loadFailed') {
    return (
      <p className="error" role="alert">
        {t('contacts.loadFailed')}{' '}
        <button type="button" onClick={state.reload}>
          {t('contacts.retry')}
        </button>
      </p>
    );
  }

  return null;
}
